import React, { useState, useEffect, useRef } from 'react';
import { X, Send, RotateCcw, AlertTriangle } from 'lucide-react';
import { supabase } from '../../../lib/supabase';
import { format } from 'date-fns';
import { sr } from 'date-fns/locale';

const NegotiationDetailPanel = ({ negotiation, onClose, onSuspend, onRestore, saving }) => {
  const [messages, setMessages] = useState([]);
  const [loadingMessages, setLoadingMessages] = useState(false);
  const [adminMessage, setAdminMessage] = useState('');
  const [sendingMsg, setSendingMsg] = useState(false);
  const [suspendReason, setSuspendReason] = useState('');
  const [showSuspendForm, setShowSuspendForm] = useState(false);
  const messagesEndRef = useRef(null);

  useEffect(() => {
    if (negotiation?.id) fetchMessages();
  }, [negotiation?.id]);

  useEffect(() => {
    messagesEndRef?.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const fetchMessages = async () => {
    setLoadingMessages(true);
    try {
      const { data, error } = await supabase?.from('negotiation_messages')?.select(`
          id, message_content, message_type, created_at, sender_id,
          sender:user_profiles!sender_id(id, full_name)
        `)?.eq('negotiation_id', negotiation?.id)?.order('created_at', { ascending: true });
      if (error) throw error;
      setMessages(data || []);
    } catch (err) {
      console.error('Error fetching messages:', err);
    } finally {
      setLoadingMessages(false);
    }
  };

  const handleSendAdminMessage = async () => {
    if (!adminMessage?.trim()) return;
    setSendingMsg(true);
    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      const { error } = await supabase?.from('negotiation_messages')?.insert({
        negotiation_id: negotiation?.id,
        sender_id: user?.id,
        message_type: 'system',
        message_content: `[ADMIN] ${adminMessage?.trim()}`,
      });
      if (error) throw error;
      setAdminMessage('');
      await fetchMessages();
    } catch (err) {
      console.error('Error sending admin message:', err);
    } finally {
      setSendingMsg(false);
    }
  };

  const handleSuspend = () => {
    if (!suspendReason?.trim()) return;
    onSuspend(negotiation?.id, suspendReason);
    setShowSuspendForm(false);
  };

  return (
    <div className="h-full flex flex-col bg-white">
      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b border-gray-200 bg-gray-50">
        <div>
          <h3 className="font-semibold text-gray-900">Detalji pregovora</h3>
          <p className="text-xs text-gray-500">{negotiation?.buyerName} ↔ {negotiation?.sellerName}</p>
        </div>
        <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-gray-200 transition-colors">
          <X className="w-4 h-4 text-gray-500" />
        </button>
      </div>
      {/* Meta */}
      <div className="p-3 bg-blue-50 border-b border-blue-100 text-xs space-y-1">
        <p><span className="font-medium text-gray-600">Predmet:</span> <span className="text-gray-900">{negotiation?.offerName || 'N/A'}</span></p>
        <p><span className="font-medium text-gray-600">Datum:</span> <span className="text-gray-900">{negotiation?.createdAt ? format(new Date(negotiation?.createdAt), 'dd.MM.yyyy HH:mm', { locale: sr }) : '-'}</span></p>
        {negotiation?.moderatorNote && (
          <p><span className="font-medium text-orange-600">Napomena admina:</span> <span className="text-orange-700">{negotiation?.moderatorNote}</span></p>
        )}
      </div>
      {/* Messages */}
      <div className="flex-1 overflow-y-auto p-3 space-y-2">
        {loadingMessages ? (
          <div className="flex justify-center py-8">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600" />
          </div>
        ) : messages?.length === 0 ? (
          <p className="text-center text-gray-400 text-sm py-8">Nema poruka</p>
        ) : (
          messages?.map((msg) => {
            const isAdmin = msg?.message_content?.startsWith('[ADMIN]');
            return (
              <div key={msg?.id} className={`rounded-lg p-2.5 text-sm ${
                isAdmin ? 'bg-orange-50 border border-orange-200' : 'bg-gray-50'
              }`}>
                <div className="flex items-center justify-between mb-1">
                  <span className={`font-medium text-xs ${
                    isAdmin ? 'text-orange-700' : 'text-gray-700'
                  }`}>
                    {isAdmin ? '🛡️ Admin' : (msg?.sender?.full_name || 'Korisnik')}
                  </span>
                  <span className="text-xs text-gray-400">
                    {format(new Date(msg?.created_at), 'dd.MM HH:mm', { locale: sr })}
                  </span>
                </div>
                <p className="text-gray-800">{msg?.message_content}</p>
              </div>
            );
          })
        )}
        <div ref={messagesEndRef} />
      </div>
      {/* Admin message input */}
      <div className="p-3 border-t border-gray-200">
        <div className="flex gap-2 mb-3">
          <input
            value={adminMessage}
            onChange={(e) => setAdminMessage(e?.target?.value)}
            onKeyDown={(e) => e?.key === 'Enter' && !e?.shiftKey && handleSendAdminMessage()}
            placeholder="Admin poruka (biće označena [ADMIN])..."
            className="flex-1 border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
          <button
            onClick={handleSendAdminMessage}
            disabled={!adminMessage?.trim() || sendingMsg}
            className="p-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors disabled:opacity-50"
          >
            <Send className="w-4 h-4" />
          </button>
        </div>

        {/* Suspend/Restore */}
        {negotiation?.isArchived ? (
          <button
            onClick={() => onRestore(negotiation?.id)}
            disabled={saving}
            className="w-full flex items-center justify-center gap-2 bg-green-600 hover:bg-green-700 text-white py-2.5 rounded-lg text-sm font-medium transition-colors disabled:opacity-50"
          >
            <RotateCcw className="w-4 h-4" />
            Vrati pregovor
          </button>
        ) : showSuspendForm ? (
          <div className="space-y-2">
            <textarea
              value={suspendReason}
              onChange={(e) => setSuspendReason(e?.target?.value)}
              placeholder="Razlog suspenzije..."
              rows={2}
              className="w-full border border-red-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-red-400 resize-none"
            />
            <div className="flex gap-2">
              <button
                onClick={handleSuspend}
                disabled={!suspendReason?.trim() || saving}
                className="flex-1 bg-red-600 hover:bg-red-700 text-white py-2 rounded-lg text-sm font-medium transition-colors disabled:opacity-50"
              >
                Potvrdi suspenziju
              </button>
              <button
                onClick={() => setShowSuspendForm(false)}
                className="px-3 py-2 border border-gray-300 rounded-lg text-sm text-gray-700 hover:bg-gray-50"
              >
                Otkaži
              </button>
            </div>
          </div>
        ) : (
          <button
            onClick={() => setShowSuspendForm(true)}
            className="w-full flex items-center justify-center gap-2 bg-red-50 hover:bg-red-100 text-red-700 border border-red-200 py-2.5 rounded-lg text-sm font-medium transition-colors"
          >
            <AlertTriangle className="w-4 h-4" />
            Suspenduj / Arhiviraj pregovor
          </button>
        )}
      </div>
    </div>
  );
};

export default NegotiationDetailPanel;
